/**
 * Every cue but one shares the ChooseChow chime. `new_order` is the chef's
 * "a customer just paid" alert and has its own synthesized bell, because it is
 * the one sound a busy kitchen must not mistake for a message.
 */
export type SoundCue = 'action' | 'message' | 'order' | 'success' | 'new_order'
export const SOUND_CUES: readonly SoundCue[] = ['action', 'message', 'order', 'success', 'new_order']
const CHIME_URL = '/tunes/choose-chow-tune-3.mp3'
/** The shared chime is trimmed to this length and faded, never hard-cut. */
const CHIME_SECONDS = 0.7
const CHIME_FADE_SECONDS = 0.12

/**
 * The new-order bell: a rising G major arpeggio (G5 C6 E6 G6), rung twice.
 *
 * Bright enough to cut through a kitchen extractor, but built from sine
 * partials with a soft 6ms attack and a natural exponential decay, so it reads
 * as a bell rather than an alarm. Synthesized, so it needs no download and
 * cannot fail to load.
 */
const NEW_ORDER_NOTES = [783.99, 1046.5, 1318.51, 1567.98] as const
const NEW_ORDER_NOTE_GAP = 0.085
const NEW_ORDER_NOTE_DECAY = 0.9
const NEW_ORDER_REPEAT_AT = 1.15
/** Bell partials as [frequency ratio, relative level]. */
const BELL_PARTIALS = [[1, 1], [2, 0.32], [3, 0.12], [4.2, 0.05]] as const

/**
 * Schedule the new-order bell on a running context.
 *
 * Exported for tests. Returns the total length in seconds.
 */
export const scheduleNewOrderBell = (context: BaseAudioContext, destination: AudioNode, volume: number, at = context.currentTime): number => {
  const master = context.createGain()
  // A gentle high cut keeps the upper partials sweet on phone speakers.
  const tone = context.createBiquadFilter()
  tone.type = 'lowpass'
  tone.frequency.value = 7000
  master.gain.value = clampVolume(volume)
  master.connect(tone)
  tone.connect(destination)
  let end = at
  for (const round of [0, NEW_ORDER_REPEAT_AT]) {
    NEW_ORDER_NOTES.forEach((frequency, index) => {
      const start = at + round + index * NEW_ORDER_NOTE_GAP
      // The last note of each round rings longest, so the phrase resolves.
      const decay = index === NEW_ORDER_NOTES.length - 1 ? NEW_ORDER_NOTE_DECAY * 1.4 : NEW_ORDER_NOTE_DECAY
      for (const [ratio, level] of BELL_PARTIALS) {
        const osc = context.createOscillator()
        const env = context.createGain()
        osc.type = 'sine'
        osc.frequency.value = frequency * ratio
        // Higher partials die faster, which is what makes a struck bell.
        const partialDecay = decay / ratio
        env.gain.setValueAtTime(0.0001, start)
        env.gain.exponentialRampToValueAtTime(0.28 * level, start + 0.006)
        env.gain.exponentialRampToValueAtTime(0.0001, start + partialDecay)
        osc.connect(env)
        env.connect(master)
        osc.start(start)
        osc.stop(start + partialDecay + 0.02)
        osc.onended = () => { osc.disconnect(); env.disconnect() }
        end = Math.max(end, start + partialDecay + 0.02)
      }
    })
  }
  return end - at
}
export interface SoundPreferences {
  enabled: boolean
  volume: number
}

/**
 * The volume range, declared once.
 *
 * The stored preference and the settings slider must agree on the ceiling. When
 * they disagreed, the slider still used the old maximum while storage accepted
 * the new one, so a default volume above that maximum rendered as "Volume · 123%"
 * on a control whose thumb could not reach the value it was showing.
 */
export const MIN_VOLUME = 0
export const MAX_VOLUME = 0.5

/** A stored volume, clamped into the supported range. */
export const clampVolume = (volume: number): number =>
  Math.max(MIN_VOLUME, Math.min(MAX_VOLUME, volume))

export const DEFAULT_SOUND_PREFERENCES: SoundPreferences = { enabled: true, volume: 0.35 }

export const readSoundPreferences = (profileId: string): SoundPreferences => {
  try {
    const raw = JSON.parse(localStorage.getItem(`chow:sounds:${profileId}`) || '{}')
    return {
      enabled: typeof raw.enabled === 'boolean' ? raw.enabled : true,
      volume:
        typeof raw.volume === 'number' && Number.isFinite(raw.volume)
          ? clampVolume(raw.volume)
          : DEFAULT_SOUND_PREFERENCES.volume,
    }
  } catch {
    return { ...DEFAULT_SOUND_PREFERENCES }
  }
}

export const saveSoundPreferences = (profileId: string, preferences: SoundPreferences) => {
  try {
    localStorage.setItem(`chow:sounds:${profileId}`, JSON.stringify(preferences))
  } catch {
    /* This tab still receives the setting. */
  }
  window.dispatchEvent(
    new CustomEvent('chow:sound-preferences', { detail: { profileId, preferences } })
  )
}

/** Duplicate events and bursts must not become a chorus. */
export class CueGate {
  private seen = new Map<string, number>()
  private last = -Infinity
  allow(key: string, now: number, important = false): boolean {
    if (this.seen.has(key)) return false
    this.seen.set(key, now)
    if (this.seen.size > 256) this.seen.delete(this.seen.keys().next().value!)
    if (now - this.last < (important ? 1200 : 3000)) return false
    this.last = now
    return true
  }
}

export class SoundPlayer {
  private context?: AudioContext
  private fallbackBuffer?: AudioBuffer
  private pending?: Promise<void>
  private source?: AudioBufferSourceNode
  private abort = new AbortController()
  private disposed = false
  private gate = new CueGate()
  // New-order bells keep their own burst window, so a message chime a moment
  // earlier can never swallow a paid order.
  private urgentGate = new CueGate()

  async unlock(): Promise<void> {
    if (this.disposed) return
    try {
      this.context ??= new AudioContext()
      await this.context.resume()
      this.pending ??= this.load()
      await this.pending
    } catch {
      /* Unsupported or blocked audio remains silent. */
    }
  }

  private async load(): Promise<void> {
    const timer = window.setTimeout(() => this.abort.abort(), 6000)
    try {
      const res = await fetch(CHIME_URL, { signal: this.abort.signal })
      if (!res.ok || !this.context) return
      const bytes = await res.arrayBuffer()
      if (bytes.byteLength > 400_000 || this.disposed) return
      this.fallbackBuffer = await this.context.decodeAudioData(bytes)
    } finally {
      window.clearTimeout(timer)
    }
  }

  stop(): void {
    try {
      this.source?.stop()
    } catch {
      /* A finished source needs no stop. */
    }
    this.source = undefined
  }

  play(cue: SoundCue, key: string, preferences: SoundPreferences, preview = false): boolean {
    if (this.disposed || (!preview && !preferences.enabled)) return false
    // A new order rings even when the kitchen screen is not focused or is
    // behind another tab: a chef glancing away is exactly when it matters.
    // Every other cue stays polite and plays only for the window in use.
    const urgent = cue === 'new_order'
    if (!urgent && document.visibilityState !== 'visible') return false
    if (!urgent && !preview && !document.hasFocus()) return false
    if (!this.context || this.context.state !== 'running') return false
    if (urgent) {
      if (!this.urgentGate.allow(key, Date.now(), true)) return false
      return this.ring(preferences)
    }
    const buffer = this.fallbackBuffer
    if (!buffer) return false
    if (!this.gate.allow(key, Date.now())) return false
    this.stop()
    const source = this.context.createBufferSource()
    const gain = this.context.createGain()
    const volume = clampVolume(preferences.volume)
    const length = Math.min(buffer.duration, CHIME_SECONDS)
    const now = this.context.currentTime
    // Fade out instead of cutting mid-waveform, which clicked on every cue.
    gain.gain.setValueAtTime(volume, now)
    gain.gain.setValueAtTime(volume, now + Math.max(0, length - CHIME_FADE_SECONDS))
    gain.gain.linearRampToValueAtTime(0.0001, now + length)
    source.buffer = buffer
    source.connect(gain)
    gain.connect(this.context.destination)
    source.onended = () => {
      source.disconnect()
      gain.disconnect()
      if (this.source === source) this.source = undefined
    }
    this.source = source
    source.start(0, 0, length)
    return true
  }

  /**
   * Ring the new-order bell again for an order still waiting. Repeats share
   * one key by design, so they bypass the duplicate gate that play() applies.
   */
  ring(preferences: SoundPreferences): boolean {
    if (this.disposed || !preferences.enabled) return false
    if (!this.context || this.context.state !== 'running') return false
    this.stop()
    scheduleNewOrderBell(this.context, this.context.destination, preferences.volume)
    return true
  }

  pause(): void {
    this.stop()
    void this.context?.suspend().catch(() => {})
  }

  dispose(): void {
    this.disposed = true
    this.abort.abort()
    this.stop()
    this.fallbackBuffer = undefined
    void this.context?.close().catch(() => {})
  }
}

export const emitSound = (cue: SoundCue, key: string) => {
  if (typeof window !== 'undefined')
    window.dispatchEvent(new CustomEvent('chow:feedback', { detail: { cue, key } }))
}
